/**
 * Lien Verifications Hook
 *
 * React Query hook for fetching and managing lien verification records.
 * Calls the lien verification API at /api/v1/property/liens.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PropertyTitleAdmin } from '@aaimplatform/institutional-types';
import { propertyTitleAdminClient } from '../api/typed-clients';
import { apiErrorMessage } from '../api/client';

export type LienVerification = PropertyTitleAdmin.components['schemas']['LienVerification'];
export type LienVerificationListResponse = PropertyTitleAdmin.components['schemas']['LienVerificationListResponse'];
export type LienRiskLevel = PropertyTitleAdmin.components['schemas']['LienRiskLevel'];
export type CreateLienVerificationRequest = PropertyTitleAdmin.components['schemas']['CreateLienVerificationRequest'];

const QUERY_KEY = 'lienVerifications';

/**
 * Hook for listing lien verification records.
 */
export function useLienVerifications(params?: {
  riskLevel?: LienRiskLevel;
  tenantId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: async (): Promise<LienVerificationListResponse> => {
      const result = await propertyTitleAdminClient.GET('/title/lien-verifications', {
        params: {
          query: {
            riskLevel: params?.riskLevel,
            tenantId: params?.tenantId,
            limit: params?.limit,
            offset: params?.offset,
          },
        },
      });

      if (result.error || !result.data) {
        const status = result.response?.status ?? 0;
        const statusText = result.response?.statusText ?? 'Unknown';
        throw new Error(apiErrorMessage(result.error) ?? `HTTP ${status}: ${statusText}`);
      }

      return result.data;
    },
  });
}

/**
 * Hook for creating a new lien verification by property address.
 */
export function useCreateLienVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateLienVerificationRequest): Promise<LienVerification> => {
      const result = await propertyTitleAdminClient.POST('/title/lien-verifications', {
        body: request,
      });

      if (result.error || !result.data) {
        const status = result.response?.status ?? 0;
        const statusText = result.response?.statusText ?? 'Unknown';
        throw new Error(apiErrorMessage(result.error) ?? `HTTP ${status}: ${statusText}`);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
